<template>
  <div class="return-repl-panel">
    <div class="box-title">
      <h4>
        <i class="fal fa-undo"></i>
        ຕີກັບໄປສາຂາທົດແທນ (Return Replacement Branch)
      </h4>
    </div>
    <p class="return-repl-hint">
      ປົກກະຕິການຕີກັບຈະສົ່ງພັດສະດຸກັບໄປສາຂາຕົ້ນທາງຂອງບິນເອງ. ຖ້າສາຂາຕົ້ນທາງນັ້ນຖືກ<b>ປິດຊົ່ວຄາວ</b>
      ແມ່ນບໍ່ມີໃຜຢູ່ຮັບ ລະບົບຈຶ່ງບໍ່ອະນຸຍາດໃຫ້ຕີກັບ. ຕັ້ງສາຂາທົດແທນຢູ່ບ່ອນນີ້ ເພື່ອໃຫ້ການຕີກັບຂອງສາຂານີ້
      ຖືກສົ່ງໄປສາຂາໃໝ່ນັ້ນແທນ — ສາຂາທົດແທນຈະກາຍເປັນສາຂາປາຍທາງຂອງບິນຫຼັງຕີກັບ ແລະ
      ປະຫວັດ, ລາຍການບັນຫາພັດສະດຸ ແລະ ສະຖານະຂອງມັນຈະໃຊ້ສາຂາໃໝ່ນີ້ຕາມ.
    </p>

    <div v-if="loadError" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-exclamation-triangle" :style="{ marginRight: '6px' }"></i>
      ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້ — ລະບົບຫຼັງບ້ານອາດຍັງບໍ່ຮອງຮັບ ຫຼື ເກີດຂໍ້ຜິດພາດ. ກະລຸນາລອງໃໝ່ພາຍຫຼັງ.
      <vs-button transparent :style="{ display: 'inline-block', marginLeft: '8px' }" @click="retryLoad()">
        ລອງໃໝ່
      </vs-button>
    </div>

    <div v-else-if="info.schema_ready === false" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-database" :style="{ marginRight: '6px' }"></i>
      ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມສຳລັບ feature ນີ້ (migration ຍັງບໍ່ໄດ້ດຳເນີນ) —
      ການຕີກັບຍັງເຮັດວຽກຄືເກົ່າທຸກຢ່າງ.
    </div>

    <!-- The write landed but the shared cache could not be invalidated, so the return path may
         keep resolving against the previous configuration for up to 5 minutes. -->
    <CacheStaleNotice :visible="cacheStale">ການຕີກັບອາດຍັງໃຊ້ຄ່າເກົ່າ</CacheStaleNotice>

    <div class="columns is-multiline" :style="{ marginTop: '10px' }">
      <div class="column is-4">
        <div class="return-repl-card">
          <label class="label">ສະຖານະສາຂານີ້</label>
          <p class="return-repl-value">
            <i :class="closureIcon" :style="{ marginRight: '6px' }"></i>
            {{ closureLabel }}
          </p>
          <p class="return-repl-note">{{ info.branch_name || '-' }}</p>
        </div>
      </div>
      <div class="column is-5">
        <div class="return-repl-card" :class="{ 'is-effective': info.is_effective }">
          <label class="label">ສາຂາທີ່ຮັບການຕີກັບແທນ (ທີ່ຕັ້ງໄວ້)</label>
          <p class="return-repl-value">
            {{ hasActiveReplacement ? info.replacement.replacement_branch_name : '-' }}
          </p>

          <!--
            Configured is NOT the same as in force. Which of the two this is, and why, is decided
            by the backend and arrives as `dormant_reason` — every input is a backend rule (a
            tinyint closure column plus a carve-out, the ReturnBranchGroup diversion lists), and a
            client-side restatement of any of them rots silently the moment one changes. Render the
            reason; never re-derive it.
          -->
          <p v-if="isEffectiveAtConfiguredBranch" class="return-repl-note is-on">
            <i class="fal fa-check-circle" :style="{ marginRight: '4px' }"></i>
            ກຳລັງໃຊ້ງານ — ການຕີກັບຂອງສາຂານີ້ຈະໄປສາຂາທົດແທນນີ້
          </p>
          <p v-else-if="isEffectiveElsewhere" class="return-repl-note is-warn">
            <i class="fal fa-exclamation-triangle" :style="{ marginRight: '4px' }"></i>
            ສາຂາທົດແທນທີ່ຕັ້ງໄວ້ຖືກປິດແລ້ວ — ການຕີກັບຈະໄປຕໍ່ທີ່
            <b>{{ info.effective_branch_name }}</b> ຕາມລູກໂສ້ທົດແທນ.
            ຄວນປ່ຽນມາຕັ້ງສາຂານັ້ນໂດຍກົງ.
          </p>
          <p v-else-if="info.dormant_reason === 'branch_open'" class="return-repl-note is-muted">
            ຕັ້ງໄວ້ແລ້ວ ແຕ່ຍັງບໍ່ມີຜົນ — ສາຂານີ້ຍັງເປີດປົກກະຕິ ການຕີກັບຈຶ່ງກັບມາສາຂານີ້ຄືເກົ່າ.
            ຈະມີຜົນທັນທີເມື່ອສາຂານີ້ຖືກປິດຊົ່ວຄາວ.
          </p>
          <p v-else-if="info.dormant_reason === 'permanently_closed'" class="return-repl-note is-warn">
            <i class="fal fa-exclamation-triangle" :style="{ marginRight: '4px' }"></i>
            ຕັ້ງໄວ້ແລ້ວ ແຕ່ບໍ່ຖືກໃຊ້ — ສາຂານີ້ປິດຖາວອນ ການຕີກັບຈະໄປສາຂາກວດສອບ (371) ຕາມກົດເດີມ
          </p>
          <p v-else-if="info.dormant_reason === 'always_diverted'" class="return-repl-note is-warn">
            <i class="fal fa-exclamation-triangle" :style="{ marginRight: '4px' }"></i>
            ຕັ້ງໄວ້ແລ້ວ ແຕ່ບໍ່ຖືກໃຊ້ — ການຕີກັບຂອງສາຂານີ້ຖືກສົ່ງໄປສາຂາສະເພາະ (ກວດສອບ 371 ຫຼື ສາຂາ Shopee) ຢູ່ແລ້ວ
            ຈຶ່ງບໍ່ຜ່ານສາຂາທົດແທນ.
          </p>
          <!-- Configured, the branch IS temporarily closed, and still nothing resolves: the
               replacement cannot receive and neither can anything it points at, so returns are
               refused exactly as they were before this was set up. That must not read as "-". -->
          <p v-else-if="info.dormant_reason === 'no_usable_branch'" class="return-repl-note is-danger">
            <i class="fal fa-times-circle" :style="{ marginRight: '4px' }"></i>
            ຕັ້ງໄວ້ແລ້ວ ແຕ່ໃຊ້ບໍ່ໄດ້ — ສາຂາທົດແທນ (ແລະ ສາຂາທີ່ມັນສົ່ງຕໍ່) ຖືກປິດໝົດ
            ການຕີກັບຂອງສາຂານີ້ຈຶ່ງຍັງເຮັດບໍ່ໄດ້. ກະລຸນາເລືອກສາຂາທີ່ຍັງເປີດ.
          </p>
          <p v-else-if="hasInactiveReplacement" class="return-repl-note is-muted">
            ເຄີຍຕັ້ງເປັນ {{ info.replacement.replacement_branch_name || ('#' + info.replacement.replacement_branch_id) }}
            (ຍົກເລີກແລ້ວ)
          </p>
          <p v-else class="return-repl-note is-muted">
            ຍັງບໍ່ໄດ້ຕັ້ງສາຂາທົດແທນ
          </p>

          <p v-if="hasActiveReplacement" class="return-repl-note is-muted">
            ອັບເດດຫຼ້າສຸດ: {{ info.replacement.updated_at || '-' }}
          </p>
        </div>
      </div>
    </div>

    <div class="columns is-multiline">
      <div class="column is-5">
        <div class="field">
          <div class="control">
            <label class="label">
              ເລືອກສາຂາທົດແທນ (ຮັບການຕີກັບແທນສາຂານີ້)
              <span class="text-danger">{{ server_errors.replacementBranchId }}</span>
            </label>
            <model-list-select
                :list="branchOptionList"
                v-model="replacementBranchId"
                name="replacementBranchId"
                placeholder="ກະລຸນາເລືອກສາຂາທົດແທນ..."
                option-text="label"
                option-value="id">
            </model-list-select>
          </div>
        </div>
      </div>
      <div class="column is-6 return-repl-actions">
        <vs-button :loading="btnLoading" :disabled="!replacementBranchId || !canMutate" @click="SaveReplacement()">
          <i class="fal fa-check-circle" :style="{ marginRight: '6px' }"></i>
          ບັນທຶກສາຂາທົດແທນ
        </vs-button>
        <vs-button v-if="hasActiveReplacement" danger flat
                   :loading="removeLoading" :disabled="!canMutate"
                   @click="removeModalActive = true">
          <i class="fad fa-trash-alt" :style="{ marginRight: '6px' }"></i>
          ຍົກເລີກ (ກັບໄປໃຊ້ກົດເດີມ)
        </vs-button>
      </div>
    </div>

    <Loading v-if="isLoading"/>

    <vs-dialog prevent-close v-model="removeModalActive">
      <div class="con-content" :style="{ marginTop: '15px' }">
        <p :style="{ fontSize: '18px', color: '#195BFF' }">
          ກະລຸນາກົດຢືນຢັນ ເພື່ອຍົກເລີກສາຂາທົດແທນ
          <b>{{ hasActiveReplacement ? info.replacement.replacement_branch_name : '' }}</b>
        </p>
        <p :style="{ marginTop: '8px', color: '#888' }">
          ຫຼັງຍົກເລີກ ຖ້າສາຂານີ້ຍັງປິດຢູ່ ບິນຂອງມັນຈະຕີກັບບໍ່ໄດ້ຄືເກົ່າ.
        </p>
      </div>
      <div class="con-footer" :style="{ marginTop: '25px' }">
        <vs-button
            :loading="removeLoading"
            transparent :style="{ color: 'crimson' }"
            @click="RemoveReplacement()"
        >
          ຢືນຢັນ
        </vs-button>
        <vs-button @click="removeModalActive = false" dark transparent>
          ຍົກເລີກ
        </vs-button>
      </div>
    </vs-dialog>
  </div>
</template>
<script>
import { ModelListSelect } from 'vue-search-select';
import Loading from '@/views/Loading';
import CacheStaleNotice from '@/components/CacheStaleNotice';

export default {
  components: { ModelListSelect, Loading, CacheStaleNotice },

  data: () => ({
    isLoading: false,
    loadError: false,
    // Last mutation committed but its cache invalidation failed — the return path can lag
    // behind the saved configuration until the backend TTL expires.
    cacheStale: false,
    btnLoading: false,
    removeLoading: false,
    removeModalActive: false,
    replacementBranchId: '',
    replacementOptions: [],
    info: {
      branch_id: null,
      branch_name: '',
      is_closed: false,
      is_permanently_closed: false,
      schema_ready: true,
      is_effective: false,
      effective_branch_id: null,
      effective_branch_name: null,
      dormant_reason: null,
      replacement: null,
    },
    server_errors: {
      replacementBranchId: '',
    },
  }),

  computed: {
    branchId() {
      return this.$route.params.id;
    },
    hasActiveReplacement() {
      return !!(this.info.replacement && this.info.replacement.is_active);
    },
    hasInactiveReplacement() {
      return !!(this.info.replacement && !this.info.replacement.is_active);
    },
    // Every mutation needs the backend schema in place; without it the API answers 422 and the
    // buttons would only produce error toasts.
    canMutate() {
      return !this.loadError && this.info.schema_ready !== false;
    },
    closureLabel() {
      if (this.info.is_permanently_closed) {
        return 'ປິດຖາວອນ';
      }
      return this.info.is_closed ? 'ປິດຊົ່ວຄາວ' : 'ເປີດປົກກະຕິ';
    },
    closureIcon() {
      if (this.info.is_permanently_closed) {
        return 'fal fa-ban';
      }
      return this.info.is_closed ? 'fal fa-pause-circle' : 'fal fa-check-circle';
    },
    // Is the branch returns actually go to the one that was configured?
    isEffectiveAtConfiguredBranch() {
      return this.info.is_effective
          && this.hasActiveReplacement
          && String(this.info.effective_branch_id) === String(this.info.replacement.replacement_branch_id);
    },
    // Configured branch is closed, but a further hop in the chain still resolves — returns work,
    // just not where the row says.
    isEffectiveElsewhere() {
      return this.info.is_effective && !this.isEffectiveAtConfiguredBranch;
    },
    // The option list is built by the backend, which owns the definition of "closed" (a tinyint
    // column plus a carve-out). Only the self-exclusion is decided here, because only the client
    // knows which branch is being edited.
    branchOptionList() {
      return this.replacementOptions
          .filter(item => String(item.id) !== String(this.branchId));
    },
  },

  created() {
    this.fetchData();
    this.fetchOptions();
  },

  methods: {
    retryLoad() {
      this.loadError = false;
      this.fetchData();
      this.fetchOptions();
    },
    fetchData() {
      this.isLoading = true;
      /// A fresh load of the current server state is where a warning about a PAST save stops
      /// being useful — by then the cache has usually expired, and the cards below show the
      /// configuration actually in force.
      this.cacheStale = false;
      this.$axios.get('get-branch-return-replacement/' + this.branchId).then(res => {
        this.info = res.data;
        this.isLoading = false;
      }).catch(() => {
        // Backend not deployed yet / outage: say so instead of rendering empty values as if the
        // branch were simply unconfigured.
        this.isLoading = false;
        this.loadError = true;
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      });
    },
    fetchOptions() {
      this.$axios.get('list-branch-return-replacement-options').then(res => {
        this.replacementOptions = res.data.replacementOptions || [];
      }).catch(() => {
        this.loadError = true;
      });
    },
    SaveReplacement() {
      if (!this.replacementBranchId) {
        return;
      }
      this.server_errors.replacementBranchId = '';
      this.btnLoading = true;
      this.$axios.post('set-branch-return-replacement/' + this.branchId, {
        replacementBranchId: this.replacementBranchId,
      }).then(res => {
        this.btnLoading = false;
        if (res.data.success == true) {
          this.info = res.data;
          this.replacementBranchId = '';
          this.cacheStale = res.data.cache_stale === true;

          /// Saved either way — but when the cache could not be dropped, the operator has to know
          /// the return path may lag, or they will read a still-refused return as a failed save.
          if (this.cacheStale) {
            this.$notification.openNotification(
              { title: 'ສາຂາທົດແທນ', text: 'ບັນທຶກແລ້ວ ແຕ່ຍັງບໍ່ທັນມີຜົນທຸກບ່ອນ (~5 ນາທີ)' },
              'top-right', 'warn', 7000
            );
          } else {
            this.$notification.OpenNotification_EditItem_OnSuccess('top-right', 'dark', 3000);
          }
        }
      }).catch(error => {
        this.btnLoading = false;
        if (error.response && error.response.status === 422) {
          this.server_errors.replacementBranchId = this.serverErrorMessage(error.response.data?.error);
        } else {
          this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
        }
      });
    },
    RemoveReplacement() {
      this.removeLoading = true;
      this.$axios.delete('delete-branch-return-replacement/' + this.branchId).then(res => {
        this.removeLoading = false;
        this.removeModalActive = false;
        if (res.data.success == true) {
          this.info = res.data;
          this.replacementBranchId = '';
          this.cacheStale = res.data.cache_stale === true;

          if (this.cacheStale) {
            this.$notification.openNotification(
              { title: 'ສາຂາທົດແທນ', text: 'ຍົກເລີກແລ້ວ ແຕ່ຍັງບໍ່ທັນມີຜົນທຸກບ່ອນ (~5 ນາທີ)' },
              'top-right', 'warn', 7000
            );
          } else {
            this.$notification.OpenNotification_DeleteItem_OnSuccess('top-right', 'danger', 3000);
          }
        } else {
          // e.g. NOT_FOUND (already removed elsewhere) — show the refreshed state instead of
          // pretending the delete just succeeded.
          this.info = res.data;
          this.$notification.openNotification(
            { title: 'ສາຂາທົດແທນ', text: 'ບໍ່ມີສາຂາທົດແທນໃຫ້ຍົກເລີກແລ້ວ — ໂຫຼດຂໍ້ມູນຄືນໃໝ່' },
            'top-right', 'danger', 5000
          );
        }
      }).catch(error => {
        this.removeLoading = false;
        this.removeModalActive = false;
        if (error.response && error.response.status === 422) {
          this.$notification.openNotification(
            { title: 'ສາຂາທົດແທນ', text: this.serverErrorMessage(error.response.data?.error) },
            'top-right', 'danger', 5000
          );
        } else {
          this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
        }
      });
    },
    serverErrorMessage(errorCode) {
      if (errorCode === 'SCHEMA_NOT_READY') {
        return 'ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມສຳລັບ feature ນີ້ (migration ຍັງບໍ່ໄດ້ດຳເນີນ)';
      }
      if (errorCode === 'BRANCH_NOT_FOUND') {
        return 'ບໍ່ພົບສາຂານີ້ໃນລະບົບ';
      }
      if (errorCode === 'REPLACEMENT_BRANCH_NOT_FOUND') {
        return 'ບໍ່ພົບສາຂາທົດແທນທີ່ເລືອກ';
      }
      if (errorCode === 'REPLACEMENT_IS_SAME_BRANCH') {
        return 'ເລືອກສາຂາດຽວກັນບໍ່ໄດ້ — ຕ້ອງເປັນສາຂາອື່ນ';
      }
      if (errorCode === 'REPLACEMENT_BRANCH_CLOSED') {
        return 'ສາຂາທົດແທນທີ່ເລືອກຖືກປິດຢູ່ — ຕ້ອງເລືອກສາຂາທີ່ຍັງເປີດ';
      }
      if (errorCode === 'REPLACEMENT_WOULD_CYCLE') {
        return 'ຕັ້ງແບບນີ້ຈະວົນກັບມາສາຂານີ້ເອງ — ກະລຸນາເລືອກສາຂາອື່ນ';
      }
      if (errorCode === 'REPLACEMENT_REQUIRED') {
        return 'ກະລຸນາເລືອກສາຂາທົດແທນກ່ອນ';
      }
      return 'ບໍ່ສາມາດບັນທຶກໄດ້ ກະລຸນາກວດສາຂາທີ່ເລືອກຄືນ';
    },
  },
};
</script>
<style lang="scss" scoped>
.return-repl-panel {
  margin-top: 10px;

  .return-repl-hint {
    color: #666;
    font-size: 14px;
    margin-top: 6px;
    line-height: 1.7;
  }

  .return-repl-card {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px 16px;
    height: 100%;

    &.is-effective {
      border-color: #195BFF;
    }

    .return-repl-value {
      font-size: 20px;
      font-weight: 600;
      margin-top: 4px;
    }

    .return-repl-note {
      margin-top: 6px;
      font-size: 13px;
      color: #195BFF;

      &.is-on {
        color: #17a673;
      }

      &.is-warn {
        color: #d9822b;
      }

      &.is-danger {
        color: crimson;
      }

      &.is-muted {
        color: #999;
      }
    }
  }

  .return-repl-actions {
    display: flex;
    align-items: flex-end;

    .vs-button {
      margin-right: 8px;
    }
  }
}
</style>
